// Admin API client for the org-wide retention surface (item 40 of
// PRODUCT-ROADMAP.md, "AdminRetention-Page nachreichen").
//
// The per-repo retention CRUD already lives on the AppShell side
// (frontend/src/api/client.ts → useRepoRetention / createRepoRetention /
// …); the AdminRetention page is read-only across every repo, so this
// module only exposes list / audit / sweeper-health / preview hooks.

import useSWR from 'swr';
import type { RetentionRule } from './client';

export interface RetentionPolicyOrg {
  id: string;
  repo_id: string;
  repo_name: string;
  repo_slug: string;
  repo_format: string;
  name: string;
  rule: RetentionRule;
  enabled: boolean;
  dry_run: boolean;
  last_run_at?: string | null;
  created_at?: string | null;
}

export interface RetentionAuditOrg {
  artifact_path: string;
  deleted_at: string;
  dry_run: boolean;
  reason: string;
  policy_name: string;
  policy_id: string;
  repo_id: string;
  repo_slug: string;
}

export interface RetentionSweeperHealth {
  interval_seconds: number;
  enabled_policies: number;
  dry_run_policies: number;
  never_run: number;
  stale_enabled: number;
  oldest_enabled_run_at?: string | null;
  newest_enabled_run_at?: string | null;
  deleted_last_24h: number;
  dry_run_last_24h: number;
}

export interface RetentionPreviewSample {
  artifact_id: string;
  path: string;
}

export interface RetentionPreview {
  policy_id: string;
  policy_name: string;
  repo_id: string;
  repo_slug: string;
  rule: RetentionRule;
  dry_run: boolean;
  match_count: number;
  samples: RetentionPreviewSample[];
}

const json = (url: string) =>
  fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } }).then(
    (r) => {
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return r.json();
    },
  );

export function useOrgRetentionPolicies() {
  return useSWR<RetentionPolicyOrg[]>('/api/admin/retention/policies', json);
}

export function useOrgRetentionAudit(limit = 500) {
  return useSWR<RetentionAuditOrg[]>(
    `/api/admin/retention/audit?limit=${limit}`,
    json,
  );
}

export function useRetentionSweeperHealth() {
  return useSWR<RetentionSweeperHealth>('/api/admin/retention/sweeper', json, {
    refreshInterval: 30_000,
  });
}

export async function previewRetention(
  policyID: string,
): Promise<RetentionPreview> {
  const res = await fetch(
    `/api/admin/retention/preview/${encodeURIComponent(policyID)}`,
    { credentials: 'include', headers: { Accept: 'application/json' } },
  );
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}
